import { styled } from '@linaria/react';
import { memo, useCallback, useContext, useState } from 'react';
import { Handle, NodeProps, NodeToolbar, Position } from 'reactflow';
import { Stages } from '../../../api/process-client/process-client.type';
import i18n from '../../../base-dictionary';
import { useProcessStore } from '../../../store/process-store/process-store.hooks';
import { Button } from '../../../ui-library/button/button';
import { ProcessContext } from '../context/process.context';
import { Text } from '../process.styled';
import { getBackground, getBorder } from './stage-node.helpers';

const processV2Enabled = import.meta.env.VITE_FEATURE_FLAG_PROCESS_MAP_V2 === 'true';

const StageNode = memo(({ data }: NodeProps) => {
  const [showToolbar, setShowToolbar] = useState(false);
  const { setEventScope: setEventScopeStore, setRightEventScope: setRightEventScopeStore } = useProcessStore();
  const { setEventScope, setRightEventScope } = useContext(ProcessContext);

  // update both context and store to be backwards compatible with V1
  const navigateToEventGraph = () => {
    if (data.isRightGraph) {
      const eventScope = {
        showEventGraph: true,
        statuses: data.customerStages,
        statusGroup: data.title,
        selectedEdgeId: null,
      };
      setRightEventScopeStore(eventScope);
      setRightEventScope(eventScope);
    } else {
      const eventScope = {
        showEventGraph: true,
        statuses: data.customerStages,
        statusGroup: data.title,
        selectedEdgeId: null,
      };
      setEventScopeStore(eventScope);
      setEventScope(eventScope);
    }
  };

  return (
    <>
      {processV2Enabled && (
        <NodeToolbar isVisible={showToolbar} position={data.toolbarPosition || Position.Top}>
          <Button size="xs" variant="secondary" onClick={navigateToEventGraph}>
            {`${i18n.t('common.work_unit')} ${i18n.t('process.event_graph.event')} Graph`}
          </Button>
        </NodeToolbar>
      )}
      <StageNodeComponent data={data} onNodeClick={(showToolbar) => setShowToolbar(showToolbar)} />
      <Handle type="target" position={Position.Top} id="top" />
      <Handle type="source" position={Position.Bottom} id="bottom" />
      <Handle type="source" position={Position.Right} id="right" />
      <Handle type="target" position={Position.Right} id="right" />
      <Handle type="source" position={Position.Left} id="left" />
      <Handle type="target" position={Position.Left} id="left" />
    </>
  );
});

const StageNodeComponent = ({
  data,
  onNodeClick,
}: {
  data: NodeProps['data'];
  onNodeClick: (showToolbar: boolean) => void;
}) => {
  const isRightGraph = data.isRightGraph;
  const { stages: leftStages, setStages: setLeftStages, rightStages, setRightStages } = useContext(ProcessContext);
  const {
    stages: leftStagesStore,
    setStages: setLeftStagesStore,
    rightStages: rightStagesStore,
    setRightStages: setRightStagesStore,
  } = useProcessStore();

  const leftStagesToUse = processV2Enabled ? leftStagesStore : leftStages;
  const rightStagesToUse = processV2Enabled ? rightStagesStore : rightStages;
  const setLeftStagesToUse = processV2Enabled ? setLeftStagesStore : setLeftStages;
  const setRightStagesToUse = processV2Enabled ? setRightStagesStore : setRightStages;

  const stages = isRightGraph ? rightStagesToUse : leftStagesToUse;
  const setStages = isRightGraph ? setRightStagesToUse : setLeftStagesToUse;

  const onClick = useCallback(
    (stage: Stages) => {
      if (stages.includes(stage)) {
        setStages(stages.filter((s) => s !== stage));
        onNodeClick(false);
      } else {
        setStages([...stages, stage]);
        onNodeClick(true);
      }
    },
    [stages, setStages, onNodeClick],
  );

  const isActive = stages.includes(data.stage);
  const isGrayed = data.stage === Stages.Blocked || data.stage === 'Waiting';

  return (
    <NodeContainer active={isActive} grayed={isGrayed} onClick={() => onClick(data.stage)}>
      <Text fontSize={12} active={isActive} grayed={isGrayed}>
        {data.title}
      </Text>
      {data.customerStages?.map((stage: string, index: number) => (
        <Text
          key={stage}
          fontSize={14}
          fontWeight={700}
          active={isActive}
          grayed={isGrayed}
          style={{ wordWrap: 'break-word' }}
        >
          {`${stage}${index === data.customerStages.length - 1 ? '' : ','}`}
        </Text>
      ))}
    </NodeContainer>
  );
};

export { StageNode, StageNodeComponent };

const NodeContainer = styled.div<{ active: boolean; grayed: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  width: 160px;
  border-radius: 6px;
  border: 1px solid ${(props: { active: boolean; grayed: boolean }) => getBorder(props.active, props.grayed)};
  background: ${(props: { active: boolean; grayed: boolean }) => getBackground(props.active, props.grayed)};
  padding: 12px 6px;
  cursor: pointer;
`;
